import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Modal from '@/components/modal/Modal.tsx';
import ModalHeader from '@/components/modal/ModalHeader.tsx';
import ModalContent from '@/components/modal/ModalContent.tsx';
import ModalFooter from '@/components/modal/ModalFooter.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import EmailInput from '@/components/form/EmailInput.tsx';
import SingleSelectInput from '@/components/form/SingleSelectInput.tsx';
import useCreateInvitation from '@/features/dashboard/access/hooks/useCreateInvitation.ts';
import {
  inviteUserSchema,
  InviteUserFormData,
} from '@/features/dashboard/access/schemas/inviteUserSchema.ts';
import { ROLE_OPTIONS } from '@/features/auth/login/types/Role.utils.ts';

interface InviteUserModalProps {
  onClose: () => void;
}

const InviteUserModal = ({ onClose }: InviteUserModalProps) => {
  const createInvitation = useCreateInvitation();

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<InviteUserFormData>({
    resolver: zodResolver(inviteUserSchema),
  });

  const onSubmit = (data: InviteUserFormData) => {
    createInvitation.mutate(data, {
      onSuccess: () => onClose(),
    });
  };

  return (
    <Modal onClose={onClose}>
      <ModalHeader title="Zaproś użytkownika" onClose={onClose} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalContent>
          <EmailInput
            label="Adres e-mail"
            register={register('email')}
            required
            error={errors.email?.message}
          />
          <SingleSelectInput<InviteUserFormData>
            fieldName="role"
            control={control}
            options={ROLE_OPTIONS}
            label="Rola"
            required
            error={errors.role?.message}
            placeholder="Wybierz rolę..."
          />
        </ModalContent>
        <ModalFooter>
          <ActionButton tone="ghost" size="sm" onClick={onClose}>
            Anuluj
          </ActionButton>
          <ActionButton
            tone="accent"
            size="sm"
            type="submit"
            isLoading={createInvitation.isPending}
          >
            Wyślij zaproszenie
          </ActionButton>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default InviteUserModal;
